import GameContext from 'gamectx';
import { itemGetIcon, itemGetTint } from 'logic/item';
import { presetItems } from 'logic/itemTypes';
import React from 'react';

import Store from 'store';

const Context = React.createContext();

const nullPromise = Promise.resolve(null);

export function IconLibrary({ children }) {
  const cache = React.useRef({});
  const store = Store.useStore();
  const gfx = React.useContext(GameContext).gfx;

  const getIcon = React.useCallback(id => {
    const item = presetItems[id] || store.getState().items[id];
    if (!item) return nullPromise;
    let cached = cache.current[id];
    if (cached && cached.item === item && cached.hd === gfx.hd) {
      return cached.image;
    }
    const icon = itemGetIcon(item, gfx.hd);
    const tint = itemGetTint(item);
    if (cached && cached.icon === icon && cached.tint === tint && cached.hd === gfx.hd) {
      cached.item = item;
      return cached.image;
    }
    cached = cache.current[id] = {item, icon, tint, image: gfx.createIcon(icon, tint), hd: gfx.hd};
    return cached.image;
  }, [store, gfx]);

  React.useEffect(() => store.subscribe(() => {
    const items = store.getState().items;
    if (items === cache.current._items) return;
    const newCache = {_items: items};
    for (const id in items) {
      const item = items[id];
      if (!item) continue;
      const prev = cache.current[id];
      if (!prev || prev.hd !== gfx.hd) continue;
      if (prev.item === item) {
        newCache[id] = prev;
      } else {
        const icon = itemGetIcon(item, gfx.hd);
        const tint = itemGetTint(item);
        if (prev.icon === icon && prev.tint === tint) {
          prev.item = item;
          newCache[id] = prev;
        }
      }
    }
    for (const id in presetItems) {
      const prev = cache.current[id];
      if (prev && prev.hd === gfx.hd) newCache[id] = prev;
    }
    cache.current = newCache;
  }), [store, gfx]);

  return (
    <Context.Provider value={getIcon}>
      {children}
    </Context.Provider>
  );
}

export function useIconLibrary() {
  return React.useContext(Context);
}
